import { useRef, useState } from "react";

import { ChipDownContainer, Checkbox, PlainTooltip } from "@similarweb/ui-components";

import { FILTER_WIDTH, KEYS } from "app/pages/website-analysis/website-technologies/constants/constants";
import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import {
    StyledTextContainer,
    StyledCheckboxContainer,
    StyledItemInnerWrapper,
    StyledEllipsisDropdownItem,
} from "app/pages/website-analysis/website-technologies/styles";
import type { SubDomainFilterProps } from "app/pages/website-analysis/website-technologies/types";
import { getNthObjectKey } from "app/pages/workspace/sales/utils/object";

const SELECT_ALL_BTN_ID = "allItemsSelectBtn";

export const SubDomainFilter = ({ onSelect, data, onClear, disabled, appendTo }: SubDomainFilterProps) => {
    const translate = useTranslation();

    const allSubdomains = Object.keys(data || {});

    const [showApply, setShowApply] = useState(false);
    const [appliedIds, setAppliedIds] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
    const selectedIdsKeys = Object.keys(selectedIds);
    const numOfSelectedSubDomains = selectedIdsKeys.length;
    const [selectedName, setSelectedName] = useState<string>("");
    const isAllSelected = numOfSelectedSubDomains === 0 || numOfSelectedSubDomains === allSubdomains.length;

    const chipDownRef = useRef(undefined);

    const clickHandler = (item: { id: string; text: string }) => {
        setShowApply(true);

        if (item.id === SELECT_ALL_BTN_ID) {
            if (numOfSelectedSubDomains === allSubdomains.length) {
                setSelectedIds({});
            } else {
                setSelectedIds(
                    allSubdomains.reduce<Record<string, boolean>>((acc, subdomain) => {
                        acc[subdomain] = true;
                        return acc;
                    }, {}),
                );
            }
        } else {
            const isSelected = selectedIds[item.id] || false;
            if (isSelected) {
                const newSelectedIds = { ...selectedIds };
                delete newSelectedIds[item.id];
                setSelectedIds(newSelectedIds);
            } else {
                setSelectedIds({
                    ...selectedIds,
                    [item.id]: true,
                });
            }
        }
    };

    const closeHandler = () => {
        onClear();
        setSelectedIds({});
        setAppliedIds([]);
        setSelectedName("");
    };

    const toggleHandler = (_isOpen: boolean, isOutsideClick: boolean) => {
        if (showApply && isOutsideClick) {
            if (
                selectedIdsKeys.length !== appliedIds.length ||
                selectedIdsKeys.some((id) => !appliedIds.includes(id))
            ) {
                setSelectedIds(
                    appliedIds.reduce<Record<string, boolean>>((acc, subdomain) => {
                        acc[subdomain] = true;
                        return acc;
                    }, {}),
                );
            }
        }
        setShowApply(false);
    };

    const applyHandler = () => {
        onSelect(selectedIdsKeys);
        setAppliedIds(selectedIdsKeys);

        const firstSelected = getNthObjectKey(selectedIds, 0) as string;

        const selectedText = firstSelected
            ? `${firstSelected} ${numOfSelectedSubDomains > 1 ? `+${numOfSelectedSubDomains - 1}` : ""}`
            : ``;

        setSelectedName(selectedText);
        chipDownRef?.current?.closePopup();
    };

    const renderOptions = () => [
        <StyledEllipsisDropdownItem
            selected={numOfSelectedSubDomains === allSubdomains.length}
            key={SELECT_ALL_BTN_ID}
            id={SELECT_ALL_BTN_ID}
            className="CustomCheckboxDropdownItem"
        >
            <StyledItemInnerWrapper>
                <StyledTextContainer>
                    {translate("analysis.filters.subdomain.filter", {
                        numOfSelectedSubDomains: allSubdomains.length,
                    })}
                </StyledTextContainer>
                <StyledCheckboxContainer>
                    <Checkbox
                        label=""
                        selected={numOfSelectedSubDomains === allSubdomains.length}
                    />
                </StyledCheckboxContainer>
            </StyledItemInnerWrapper>
        </StyledEllipsisDropdownItem>,
        ...allSubdomains.map((name) => {
            const selected = selectedIds[name] || false;
            return (
                <StyledEllipsisDropdownItem
                    selected={selected}
                    key={name}
                    id={name}
                    text={name}
                    className="CustomCheckboxDropdownItem"
                >
                    <StyledItemInnerWrapper>
                        <StyledTextContainer>
                            {name.length > 27 ? (
                                <PlainTooltip
                                    maxWidth={260}
                                    placement="top"
                                    tooltipContent={name}
                                >
                                    <span>{`${name}`}</span>
                                </PlainTooltip>
                            ) : (
                                name
                            )}
                        </StyledTextContainer>
                        <StyledCheckboxContainer>
                            <Checkbox
                                label=""
                                selected={selected}
                            />
                        </StyledCheckboxContainer>
                    </StyledItemInnerWrapper>
                </StyledEllipsisDropdownItem>
            );
        }),
    ];

    return (
        <ChipDownContainer
            appendTo={appendTo}
            onToggle={toggleHandler}
            disabled={disabled}
            ref={chipDownRef}
            onClick={clickHandler}
            selectedIds={selectedIds}
            selectedText={selectedName}
            closeOnItemClick={false}
            buttonText={translate("analysis.filters.subdomain.filter", {
                numOfSelectedSubDomains: !isAllSelected ? numOfSelectedSubDomains : allSubdomains.length,
            })}
            searchPlaceHolder={translate(KEYS.subdomainPlaceholder)}
            onCloseItem={closeHandler}
            hasSearch={false}
            width={FILTER_WIDTH}
            tooltipDisabled
            noResultsMessage={translate("dropdown.no.results")}
            cssClassContainer="DropdownContent-container Popup-content--pro si-technologies-filter"
            footerComponent={() =>
                showApply && (
                    <FooterButton
                        onClick={applyHandler}
                        disabled={numOfSelectedSubDomains === 0}
                    >
                        {translate(KEYS.subdomainBtnApply)}
                    </FooterButton>
                )
            }
        >
            {renderOptions()}
        </ChipDownContainer>
    );
};
